// Neuigkeiten — das schwarze Brett.
// Leitung schreibt, alle lesen. Beim Öffnen gilt alles als gelesen.

import { useEffect, useState } from "react";
import { useNews, useSpeichereNews, useLoescheNews, useMarkiereGelesen, type NewsPost } from "@/lib/news";
import { useAuth } from "@/stores/authStore";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, EmptyState, Field, Input, Sheet, cn } from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";

export default function NewsPage() {
  const { data: posts = [], isLoading } = useNews();
  const role = useAuth((s) => s.role);
  const darfSchreiben = role === "admin" || role === "buero";

  const markiere = useMarkiereGelesen();
  const [bearbeiten, setBearbeiten] = useState<NewsPost | "neu" | null>(null);

  // Beim Öffnen alles als gelesen markieren — der rote Punkt verschwindet.
  useEffect(() => {
    if (!posts.length) return;
    markiere.mutate(posts.map((p) => p.id));
    // Absichtlich nur bei Längenänderung, nicht bei jedem Render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length]);

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader
        titel="Neuigkeiten"
        aktion={
          darfSchreiben ? (
            <Button variant="primary" size="sm" onClick={() => setBearbeiten("neu")}>
              + Beitrag
            </Button>
          ) : undefined
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <LogoLoader size="md" label="Neuigkeiten werden geladen" />
          </div>
        ) : posts.length === 0 ? (
          <EmptyState
            title="Noch nichts angeschlagen"
            hint={darfSchreiben ? "Schreib den ersten Beitrag." : "Hier erscheinen Mitteilungen vom Hof."}
            action={
              darfSchreiben ? (
                <Button variant="primary" onClick={() => setBearbeiten("neu")}>
                  Beitrag schreiben
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="space-y-3">
            {posts.map((p) => (
              <li key={p.id}>
                <Beitrag
                  p={p}
                  darfSchreiben={darfSchreiben}
                  onBearbeiten={() => setBearbeiten(p)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {bearbeiten && (
        <BeitragSheet
          post={bearbeiten === "neu" ? null : bearbeiten}
          onClose={() => setBearbeiten(null)}
        />
      )}
    </div>
  );
}

function Beitrag({
  p,
  darfSchreiben,
  onBearbeiten,
}: {
  p: NewsPost;
  darfSchreiben: boolean;
  onBearbeiten: () => void;
}) {
  return (
    <Card className={cn("p-4", p.pinned && "border-gruen-500/40")}>
      <div className="mb-1.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {p.pinned && (
              <span className="rounded bg-gruen-500/15 px-1.5 py-0.5 text-xs font-medium text-gruen-400">
                Angepinnt
              </span>
            )}
            <h2 className="truncate text-[15px] font-semibold text-ink-50">{p.title}</h2>
          </div>
          <p className="mt-0.5 text-xs tabular-nums text-ink-500">
            {p.author?.full_name ?? "Hof Sirges"} · {datum(p.published_at)}
          </p>
        </div>

        {darfSchreiben && (
          <button
            onClick={onBearbeiten}
            className="shrink-0 rounded-lg px-2 py-1 text-sm text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100"
          >
            Bearbeiten
          </button>
        )}
      </div>

      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-ink-200">{p.body}</p>
    </Card>
  );
}

function BeitragSheet({ post, onClose }: { post: NewsPost | null; onClose: () => void }) {
  const speichern = useSpeichereNews();
  const loeschen = useLoescheNews();
  const [title, setTitle] = useState(post?.title ?? "");
  const [body, setBody] = useState(post?.body ?? "");
  const [pinned, setPinned] = useState(post?.pinned ?? false);
  const [loeschFrage, setLoeschFrage] = useState(false);

  const kannSpeichern = title.trim().length > 2 && body.trim().length > 2;

  return (
    <Sheet
      open
      onClose={onClose}
      title={post ? "Beitrag bearbeiten" : "Neuer Beitrag"}
      footer={
        <div className="space-y-2">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!kannSpeichern}
            loading={speichern.isPending}
            onClick={async () => {
              await speichern.mutateAsync({ id: post?.id, title, body, pinned });
              onClose();
            }}
          >
            {post ? "Speichern" : "Veröffentlichen"}
          </Button>

          {post &&
            (loeschFrage ? (
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => setLoeschFrage(false)}>
                  Abbrechen
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  loading={loeschen.isPending}
                  onClick={async () => {
                    await loeschen.mutateAsync(post.id);
                    onClose();
                  }}
                >
                  Wirklich löschen
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="w-full" onClick={() => setLoeschFrage(true)}>
                Beitrag löschen
              </Button>
            ))}
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Überschrift">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Worum geht's?" autoFocus />
        </Field>

        <Field label="Text">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            placeholder="Was sollen alle wissen?"
            className="w-full rounded-lg border border-ink-700 bg-ink-900 px-3.5 py-3 text-base leading-relaxed text-ink-100 placeholder:text-ink-500 focus:border-gruen-500/60 focus:outline-none focus:ring-2 focus:ring-gruen-500/20"
          />
        </Field>

        <button
          onClick={() => setPinned((v) => !v)}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors duration-fast",
            pinned
              ? "border-gruen-500/40 bg-gruen-500/10"
              : "border-ink-700 bg-ink-900 hover:border-ink-600"
          )}
        >
          <span
            className={cn(
              "grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 text-xs font-bold",
              pinned ? "border-gruen-500 bg-gruen-500 text-gruen-on" : "border-ink-600"
            )}
          >
            {pinned && "✓"}
          </span>
          <span className="text-[15px] text-ink-100">Oben anpinnen</span>
        </button>
      </div>
    </Sheet>
  );
}

function datum(iso: string) {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
